import { useEffect, useMemo, useState } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";

import { api } from "@/lib/api";

// Shown ONCE per user: dismissal persists server-side (user.tour_dismissed_at) and in
// localStorage; never auto-opens on Video Studio; renders nothing (no overlay) when closed.
const DISMISS_KEY = "onboarding-tour-dismissed";

const ADMIN_STEPS = [
    { title: "Welcome to ACME-SIPL", body: "Turn approved SOPs into cinematic AI-led training. Powered by Sanghvi Infotech Pvt Ltd.", target: null },
    { title: "Upload a document", body: "Start by uploading a work instruction, SOP, or process document.", target: '[data-testid="nav-upload"]', route: "/knowledge/upload" },
    { title: "Review the AI extraction", body: "The AI pulls out steps, safety notes, and images. You verify what's accurate.", target: '[data-testid="nav-reviews"]', route: "/knowledge/reviews" },
    { title: "Generate a storyboard", body: "Turn approved documents into cinematic scene-by-scene training.", target: '[data-testid="nav-storyboards"]', route: "/content/storyboards" },
    { title: "Render & publish", body: "Kick off the final render, add music, and publish for learners.", target: '[data-testid="nav-video-studio"]', route: "/content/video-studio" },
    { title: "Assign learners", body: "Assign published modules to individuals or whole departments.", target: '[data-testid="nav-assignments"]', route: "/learning/assignments" },
    { title: "Ask AI", body: "Ask questions across every approved document. Answers include citations.", target: '[data-testid="nav-ask-ai"]', route: "/ai/ask" },
];

const LEARNER_STEPS = [
    { title: "Welcome to ACME-SIPL", body: "Your personalised training portal. Powered by Sanghvi Infotech Pvt Ltd.", target: null },
    { title: "Your assigned trainings", body: "Every training your organisation has assigned to you appears here.", target: null, route: "/learn" },
    { title: "Watch & take the knowledge check", body: "Reach 95% of the video, then attempt the knowledge check.", target: null, route: "/learn" },
    { title: "Your certificates", body: "Passing a knowledge check auto-issues a PDF certificate with a public verify URL.", target: null, route: "/learn/certificates" },
    { title: "Ask AI", body: "Ask questions about anything in your approved training materials. Every answer cites the source.", target: null, route: "/ai/ask" },
];

function readDismissed(userId) {
    try {
        const parsed = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
        return !!parsed[userId];
    } catch (e) { return false; }
}

function writeDismissed(userId) {
    try {
        const parsed = JSON.parse(localStorage.getItem(DISMISS_KEY) || "{}");
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ ...parsed, [userId]: Date.now() }));
    } catch (e) { /* silent */ }
    api.post("/auth/me/tour-dismissed").catch(() => {});
}

export default function OnboardingTour() {
    const { user, status } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [i, setI] = useState(0);

    const steps = useMemo(() => (
        user?.role === "LEARNER" ? LEARNER_STEPS : ADMIN_STEPS
    ), [user?.role]);

    useEffect(() => {
        if (status !== "authed" || !user) {
            setOpen(false);
            setI(0);
            return;
        }
        if (user.tour_dismissed_at || readDismissed(user.id)) return;
        if (window.location.pathname.includes("video-studio")) return;  // never auto-open here
        const t = setTimeout(() => setOpen(true), 400);
        return () => clearTimeout(t);
    }, [status, user]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") { writeDismissed(user.id); setOpen(false); } };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, user]);

    if (!open || !user) return null;
    const step = steps[i];
    const total = steps.length;

    function dismiss() {
        writeDismissed(user.id);
        setOpen(false);
    }
    function go(delta) {
        const next = i + delta;
        if (next < 0) return;
        if (next >= total) { dismiss(); return; }
        setI(next);
        if (steps[next]?.route) nav(steps[next].route);
    }

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm p-6"
            data-testid="onboarding-tour-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
        >
            <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl" data-testid="onboarding-tour">
                <button
                    onClick={dismiss}
                    className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    data-testid="tour-skip"
                    aria-label="Skip tour"
                >
                    <X size={16} />
                </button>
                <div className="px-7 pt-7 pb-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400">Step {i + 1} of {total}</div>
                    <h2 className="mt-2 text-xl font-semibold text-neutral-900" data-testid="tour-title">{step.title}</h2>
                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed" data-testid="tour-body">{step.body}</p>
                </div>
                <div className="flex items-center justify-between border-t border-neutral-100 px-7 py-4">
                    <button
                        onClick={dismiss}
                        className="text-xs font-medium text-neutral-500 hover:text-neutral-800"
                        data-testid="tour-skip-btn"
                    >
                        Skip tour
                    </button>
                    <div className="flex gap-2">
                        {i > 0 && (
                            <Button size="sm" variant="outline" onClick={() => go(-1)} data-testid="tour-back">
                                <ChevronLeft size={14} className="mr-1" />Back
                            </Button>
                        )}
                        <Button size="sm" onClick={() => go(1)} data-testid="tour-next">
                            {i + 1 === total ? "Finish" : "Next"}
                            {i + 1 !== total && <ChevronRight size={14} className="ml-1" />}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
